import "vuetify/styles";
import "@mdi/font/css/materialdesignicons.css";
import { h } from "vue";
import { createVuetify, type IconProps, type IconSet, type ThemeDefinition } from "vuetify";
import { mdi } from "vuetify/iconsets/mdi";
import { aliases, md } from "vuetify/iconsets/md";
import type { TenantBranding } from "@tenant-theme";
import { buildTenantThemes } from "@/tenant/branding";

/**
 * `metaship-backend/Static/Navbar.ts` (the NavBarList payload — Sidebar's nav
 * icons AND App Home's tile icons per Appendix A7) ships Google Material
 * Icons LIGATURE names (`light_mode`, `chevron_right`, `logout`, …), not MDI
 * slugs. This shell's own chrome uses the same convention for consistency.
 * `defaultSet: "mdi"` alone silently blanks every one of those — MDI expects
 * a hyphenated `mdi-*` class, so an underscored ligature name never matches
 * a real glyph. Composite set, same pattern as
 * `modules/portal/src/plugins/vuetify.ts`: route an explicit `mdi-*` name to
 * the MDI webfont, everything else (the Navbar.ts convention) to Material
 * Icons — requires the Material Icons webfont link in `index.html`.
 */
const metashipIcons: IconSet = {
  component: (props: IconProps) =>
    h(typeof props.icon === "string" && props.icon.startsWith("mdi-") ? mdi.component : md.component, props),
};

/**
 * Built AFTER the tenant resolves (unlike a module's static theme) — the
 * shell is the thing that publishes tenant colours, so it can't wait on
 * `@tenant-theme`'s `readTenant()` the way a hosted module does.
 */
export function createShellVuetify(branding: TenantBranding | null) {
  const { light, dark } = buildTenantThemes(branding);
  const defaultTheme = branding?.defaultTheme === "light" ? "light" : "dark";

  return createVuetify({
    icons: { defaultSet: "metaship", aliases, sets: { metaship: metashipIcons, mdi, md } },
    display: { mobileBreakpoint: 768 },
    theme: {
      defaultTheme,
      themes: {
        light: light as ThemeDefinition,
        dark: dark as ThemeDefinition,
      },
    },
    defaults: {
      global: { ripple: true },
      VTextField: { variant: "outlined", density: "compact", hideDetails: "auto" },
      VBtn: { rounded: "lg" },
    },
  });
}
